import { useState, useEffect, useRef, useCallback } from "react";
import { Spin, Tooltip, Select } from "antd";
import {
  ReloadOutlined,
  SendOutlined,
  PaperClipOutlined,
  PlusOutlined,
  MailOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
});

function relativeTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function sanitizeHtml(html) {
  if (!html) return "";
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<script/gi, "&lt;script");
}

function parseAttachments(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function avatarInitial(name) {
  return (name || "?").trim().charAt(0).toUpperCase();
}

// ---------------------------------------------------------------------------
// CustomerRow — Left pane item
// ---------------------------------------------------------------------------
function CustomerRow({ customer, selected, unreadMap, onClick }) {
  const unread = unreadMap[customer.customer_id] ?? customer.unread_count ?? 0;
  return (
    <div
      onClick={() => onClick(customer)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 13px",
        cursor: "pointer",
        borderLeft: selected ? "3px solid #667eea" : "3px solid transparent",
        background: selected ? "#f0eeff" : "transparent",
        borderBottom: "1px solid #f0f0f0",
        transition: "background 0.15s",
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 15,
          flexShrink: 0,
        }}
      >
        {avatarInitial(customer.name)}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontWeight: unread > 0 ? 700 : 500,
              fontSize: 13,
              color: "#1a1a2e",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 140,
            }}
          >
            {customer.name}
          </span>
          <span
            style={{ fontSize: 11, color: "#aaa", flexShrink: 0, marginLeft: 4 }}
          >
            {relativeTime(customer.last_email_at)}
          </span>
        </div>
        <div
          style={{
            fontSize: 11,
            color: "#999",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {customer.email}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#666",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            marginTop: 1,
          }}
        >
          {customer.last_subject || "—"}
        </div>
      </div>

      {/* Unread badge */}
      {unread > 0 && (
        <span
          style={{
            background: "#ef4444",
            color: "#fff",
            borderRadius: 10,
            fontSize: 10,
            fontWeight: 700,
            padding: "1px 6px",
            flexShrink: 0,
          }}
        >
          {unread}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MessageBubble — Thread message
// ---------------------------------------------------------------------------
function MessageBubble({ msg, isFirst }) {
  const isOut = msg.direction === "outbound";
  const attachments = parseAttachments(msg.attachments);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isOut ? "flex-end" : "flex-start",
        marginBottom: 16,
        padding: "0 18px",
      }}
    >
      {/* Meta */}
      <div
        style={{
          fontSize: 11,
          color: "#aaa",
          marginBottom: 4,
          display: "flex",
          gap: 6,
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: 600, color: isOut ? "#667eea" : "#555" }}>
          {isOut ? `You (${msg.from_email})` : msg.from_email}
        </span>
        <span>·</span>
        <span>{formatDateTime(msg.sent_at)}</span>
      </div>

      {/* Subject — first message only */}
      {isFirst && msg.subject && (
        <div
          style={{
            fontSize: 12,
            color: "#888",
            marginBottom: 5,
            fontStyle: "italic",
            maxWidth: "72%",
            alignSelf: isOut ? "flex-end" : "flex-start",
          }}
        >
          Subject: {msg.subject}
        </div>
      )}

      {/* Bubble */}
      <div
        style={{
          maxWidth: "72%",
          background: isOut
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "#ffffff",
          color: isOut ? "#fff" : "#1a1a2e",
          border: isOut ? "none" : "1px solid #e5e7eb",
          borderRadius: isOut ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          padding: "10px 14px",
          boxShadow: "0 1px 5px rgba(0,0,0,0.07)",
          fontSize: 13,
          lineHeight: 1.65,
          wordBreak: "break-word",
        }}
      >
        <div
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.body) }}
          style={{ pointerEvents: "none" }}
        />
      </div>

      {/* Delivery fail */}
      {msg.status === "failed" && isOut && (
        <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>
          ⚠ Delivery failed
        </div>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginTop: 6,
            maxWidth: "72%",
          }}
        >
          {attachments.map((att, i) => {
            const href =
              typeof att === "string" ? att : att.url || att.path || "#";
            const label =
              typeof att === "string"
                ? `Attachment ${i + 1}`
                : att.name || att.filename || `Attachment ${i + 1}`;
            return (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: isOut ? "rgba(255,255,255,0.2)" : "#f3f4f6",
                  color: isOut ? "#fff" : "#374151",
                  border: isOut
                    ? "1px solid rgba(255,255,255,0.4)"
                    : "1px solid #e5e7eb",
                  borderRadius: 6,
                  padding: "3px 9px",
                  fontSize: 11,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <PaperClipOutlined />
                {label}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function SubscriptionInbox() {
  // --- Customer list ---
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [unreadMap, setUnreadMap] = useState({});

  // --- Thread ---
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const threadEndRef = useRef(null);

  // --- Compose ---
  const [composeMode, setComposeMode] = useState("compose"); // "compose" | "template"
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [attachFiles, setAttachFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  // --- Templates ---
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);

  const internalUserId =
    Number(localStorage.getItem("trade_user_id")) || "";

  // ---------------------------------------------------------------------------
  // API: fetch customer list
  // ---------------------------------------------------------------------------
  const fetchCustomers = useCallback(async () => {
    try {
      setCustomersLoading(true);
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const res = await axios.get(
        `${API_BASE_URL}/subscription-inbox/list`,
        { params: { internal_user_id: internalUserId }, headers: authHeaders() }
      );
      if (res.data?.status === 200) {
        setCustomers(res.data.data || []);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load inbox");
      }
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // API: unread counts
  // ---------------------------------------------------------------------------
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const internalUserIdUC = Number(localStorage.getItem("trade_user_id"));
      const res = await axios.get(
        `${API_BASE_URL}/subscription-inbox/unread-count`,
        { params: { internal_user_id: internalUserIdUC }, headers: authHeaders() }
      );
      if (res.data?.status === 200) {
        const map = {};
        (res.data.by_customer || []).forEach((item) => {
          map[item.customer_id] =
            (map[item.customer_id] || 0) + item.count;
        });
        setUnreadMap(map);
      }
    } catch {
      // silent polling
    }
  }, []);

  // ---------------------------------------------------------------------------
  // API: thread for selected customer (latest conversation or by id)
  // ---------------------------------------------------------------------------
  const fetchThread = useCallback(
    async (customerId, conversationId) => {
      try {
        setThreadLoading(true);
        const internalUserIdTh = Number(localStorage.getItem("trade_user_id"));
        const params = conversationId
          ? { conversation_id: conversationId, internal_user_id: internalUserIdTh }
          : { internal_user_id: internalUserIdTh };
        const res = await axios.get(
          `${API_BASE_URL}/subscription-inbox/thread/${customerId}`,
          { headers: authHeaders(), params }
        );
        if (res.data?.status === 200) {
          const msgs = res.data.data || [];
          setThread(msgs);
          // Derive conversation_id from first message if not provided
          if (!conversationId && msgs.length > 0) {
            setActiveConversationId(msgs[0].conversation_id || null);
          } else {
            setActiveConversationId(conversationId || null);
          }
        }
      } catch (err) {
        if (err.response?.status === 401) toast.error("Session expired.");
        else toast.error("Failed to load messages");
      } finally {
        setThreadLoading(false);
      }
    },
    []
  );

  // ---------------------------------------------------------------------------
  // API: conversations list — used to get latest conversation_id
  // ---------------------------------------------------------------------------
  const fetchLatestConversation = useCallback(
    async (customerId) => {
      try {
        const internalUserIdConv = Number(localStorage.getItem("trade_user_id"));
        const res = await axios.get(
          `${API_BASE_URL}/subscription-inbox/conversations/${customerId}`,
          { params: { internal_user_id: internalUserIdConv }, headers: authHeaders() }
        );
        if (res.data?.status === 200) {
          const convs = res.data.data || [];
          return convs.length > 0 ? convs[0].conversation_id : null;
        }
      } catch {
        // silent
      }
      return null;
    },
    []
  );

  // ---------------------------------------------------------------------------
  // API: mark read
  // ---------------------------------------------------------------------------
  const markRead = useCallback(async (customerId, conversationId) => {
    try {
      const internalUserIdMR = Number(localStorage.getItem("trade_user_id"));
      await axios.patch(
        `${API_BASE_URL}/subscription-inbox/mark-read/${customerId}`,
        { conversation_id: conversationId, internal_user_id: internalUserIdMR },
        { headers: authHeaders() }
      );
      setUnreadMap((prev) => {
        const copy = { ...prev };
        delete copy[customerId];
        return copy;
      });
      setCustomers((prev) =>
        prev.map((c) =>
          c.customer_id === customerId
            ? { ...c, unread_count: 0 }
            : c
        )
      );
    } catch {
      // silent
    }
  }, []);

  // ---------------------------------------------------------------------------
  // API: email templates
  // ---------------------------------------------------------------------------
  const fetchTemplates = useCallback(async () => {
    try {
      setTemplatesLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/get-email-templates`,
        { headers: authHeaders() }
      );
      if (res.data?.status === 200) {
        setTemplates(res.data.data || []);
      }
    } catch {
      // silent
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchCustomers();
    fetchUnreadCounts();
    fetchTemplates();
  }, [fetchCustomers, fetchUnreadCounts, fetchTemplates]);

  // Poll unread every 30s
  useEffect(() => {
    const id = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(id);
  }, [fetchUnreadCounts]);

  // When customer selected — load latest thread
  useEffect(() => {
    if (!selectedCustomer) return;
    setThread([]);
    setActiveConversationId(null);
    setComposeTo(selectedCustomer.email || "");
    setComposeSubject("");
    setComposeBody("");
    setAttachFiles([]);
    setSelectedTemplateId(null);
    setTemplatePreview(null);

    (async () => {
      const convId = await fetchLatestConversation(
        selectedCustomer.customer_id
      );
      await fetchThread(selectedCustomer.customer_id, convId);
      if (convId) {
        markRead(selectedCustomer.customer_id, convId);
      }
    })();
  }, [selectedCustomer, fetchLatestConversation, fetchThread, markRead]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [thread]);

  // Template preview when selected
  useEffect(() => {
    if (!selectedTemplateId) {
      setTemplatePreview(null);
      return;
    }
    const t = templates.find((x) => x.id === selectedTemplateId);
    setTemplatePreview(t || null);
  }, [selectedTemplateId, templates]);

  // ---------------------------------------------------------------------------
  // Send
  // ---------------------------------------------------------------------------
  const handleSend = async () => {
    if (!selectedCustomer) return;
    if (composeMode === "compose" && !composeBody.trim()) return;
    if (composeMode === "template" && !selectedTemplateId) return;

    try {
      setSending(true);
      const fd = new FormData();
      fd.append("from", "info@entermark.ai");
      fd.append("to", composeTo);
      fd.append("customer_id", selectedCustomer.customer_id);
      fd.append("internal_user_id", internalUserId);
      fd.append("conversation_id", activeConversationId || "");
      for (const f of attachFiles) fd.append("attachments", f);

      let endpoint = `${API_BASE_URL}/subscription-inbox/send`;

      if (composeMode === "template") {
        endpoint = `${API_BASE_URL}/subscription-inbox/send-using-template`;
        fd.append("template_id", selectedTemplateId);
      } else {
        fd.append("subject", composeSubject);
        fd.append("body", composeBody);
      }

      const res = await axios.post(endpoint, fd, {
        headers: {
          ...authHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.status === 200 || res.status === 200) {
        toast.success("Email sent successfully");
        setComposeBody("");
        setAttachFiles([]);
        setSelectedTemplateId(null);
        setTemplatePreview(null);
        // Refresh thread
        const convId = await fetchLatestConversation(
          selectedCustomer.customer_id
        );
        await fetchThread(selectedCustomer.customer_id, convId);
        if (convId) setActiveConversationId(convId);
        fetchCustomers();
      } else {
        toast.error(res.data?.message || "Failed to send email");
      }
    } catch (err) {
      if (err.response?.status === 401) toast.error("Session expired.");
      else toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = () => {
    if (!selectedCustomer) return;
    fetchThread(selectedCustomer.customer_id, activeConversationId);
  };

  const canSend =
    !sending &&
    !!selectedCustomer &&
    composeTo.trim() !== "" &&
    (composeMode === "compose"
      ? composeBody.trim() !== ""
      : !!selectedTemplateId);

  const totalUnread = Object.values(unreadMap).reduce((a, b) => a + b, 0);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        display: "flex",
        height: "calc(100vh - 60px)",
        background: "#f8fafc",
        overflow: "hidden",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* ===== LEFT PANE — Customer list ===== */}
      <div
        style={{
          width: 280,
          minWidth: 280,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid #e5e7eb",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background:
              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            padding: "15px 14px 13px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <InboxOutlined style={{ color: "#fff", fontSize: 17 }} />
            <span
              style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}
            >
              Subscription Inbox
            </span>
            {totalUnread > 0 && (
              <span
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "1px 7px",
                  marginLeft: "auto",
                }}
              >
                {totalUnread}
              </span>
            )}
          </div>
          <div
            style={{
              color: "rgba(255,255,255,0.72)",
              fontSize: 11,
              marginTop: 3,
            }}
          >
            {customers.length} customer
            {customers.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Customer list */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {customersLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: 36,
              }}
            >
              <Spin />
            </div>
          ) : customers.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 36,
                color: "#ccc",
                gap: 8,
              }}
            >
              <MailOutlined style={{ fontSize: 32 }} />
              <span style={{ fontSize: 12 }}>No customers found</span>
            </div>
          ) : (
            customers.map((c) => (
              <CustomerRow
                key={c.customer_id}
                customer={c}
                selected={
                  selectedCustomer?.customer_id === c.customer_id
                }
                unreadMap={unreadMap}
                onClick={setSelectedCustomer}
              />
            ))
          )}
        </div>
      </div>

      {/* ===== MIDDLE PANE — Thread ===== */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid #e5e7eb",
          overflow: "hidden",
          background: "#f8fafc",
          minWidth: 0,
        }}
      >
        {/* Thread header */}
        <div
          style={{
            background: "#fff",
            borderBottom: "1px solid #e5e7eb",
            padding: "13px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          {selectedCustomer ? (
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#1a1a2e",
                }}
              >
                {selectedCustomer.name}
              </div>
              <div style={{ fontSize: 12, color: "#999", marginTop: 1 }}>
                {selectedCustomer.email} &nbsp;·&nbsp;{" "}
                {thread.length} message{thread.length !== 1 ? "s" : ""}
              </div>
            </div>
          ) : (
            <span style={{ fontSize: 14, color: "#aaa" }}>
              Select a customer
            </span>
          )}
          {selectedCustomer && (
            <Tooltip title="Refresh messages">
              <button
                onClick={handleRefresh}
                disabled={threadLoading}
                style={{
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  cursor: "pointer",
                  padding: "5px 9px",
                  color: "#555",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ReloadOutlined spin={threadLoading} />
              </button>
            </Tooltip>
          )}
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "18px 0 10px",
            background: "#f8fafc",
          }}
        >
          {!selectedCustomer ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 80,
                color: "#d1d5db",
                gap: 10,
              }}
            >
              <InboxOutlined style={{ fontSize: 52 }} />
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                Select a customer to view messages
              </span>
            </div>
          ) : threadLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                paddingTop: 60,
              }}
            >
              <Spin size="large" />
            </div>
          ) : thread.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 80,
                color: "#ccc",
                gap: 8,
              }}
            >
              <MailOutlined style={{ fontSize: 40 }} />
              <span style={{ fontSize: 13 }}>
                No messages yet — compose one!
              </span>
            </div>
          ) : (
            thread.map((msg, idx) => (
              <MessageBubble
                key={msg.id || idx}
                msg={msg}
                isFirst={idx === 0}
              />
            ))
          )}
          <div ref={threadEndRef} />
        </div>
      </div>

      {/* ===== RIGHT PANE — Compose / Reply ===== */}
      <div
        style={{
          width: 360,
          minWidth: 320,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          borderLeft: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Compose header */}
        <div
          style={{
            background:
              "linear-gradient(135deg, #5b48e8 0%, #7c3aed 100%)",
            padding: "15px 16px 13px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            <SendOutlined />
            Send Email
          </div>
          {selectedCustomer && (
            <div
              style={{
                color: "rgba(255,255,255,0.72)",
                fontSize: 11,
                marginTop: 3,
              }}
            >
              To: {selectedCustomer.name}
            </div>
          )}
        </div>

        {/* Compose body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 16px 0",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {/* Mode tab */}
          <div
            style={{
              display: "flex",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {["compose", "template"].map((tab) => (
              <button
                key={tab}
                onClick={() => setComposeMode(tab)}
                style={{
                  flex: 1,
                  background:
                    composeMode === tab
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "#fff",
                  color: composeMode === tab ? "#fff" : "#555",
                  border: "none",
                  padding: "7px 0",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {tab === "compose" ? "Compose" : "Use Template"}
              </button>
            ))}
          </div>

          {/* To field */}
          <div>
            <label
              style={{
                fontSize: 11,
                color: "#888",
                display: "block",
                marginBottom: 4,
                fontWeight: 600,
              }}
            >
              To
            </label>
            <input
              type="email"
              value={composeTo}
              onChange={(e) => setComposeTo(e.target.value)}
              placeholder="recipient@email.com"
              style={{
                width: "100%",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                padding: "7px 10px",
                fontSize: 13,
                outline: "none",
                background: "#fafbff",
                boxSizing: "border-box",
              }}
            />
          </div>

          {composeMode === "compose" ? (
            <>
              {/* Subject */}
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: "#888",
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  Subject
                </label>
                <input
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="Email subject"
                  style={{
                    width: "100%",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: "7px 10px",
                    fontSize: 13,
                    outline: "none",
                    background: "#fafbff",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Body */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <label
                  style={{
                    fontSize: 11,
                    color: "#888",
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  Message
                </label>
                <textarea
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  placeholder="Write your message here..."
                  style={{
                    flex: 1,
                    minHeight: 160,
                    width: "100%",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: "8px 10px",
                    fontSize: 13,
                    resize: "vertical",
                    outline: "none",
                    background: "#fafbff",
                    boxSizing: "border-box",
                    lineHeight: 1.6,
                  }}
                />
              </div>
            </>
          ) : (
            <>
              {/* Template select */}
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: "#888",
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  Select Template
                </label>
                <select
                  value={selectedTemplateId || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    const t = templates.find(
                      (x) => String(x.id) === val
                    );
                    setSelectedTemplateId(t ? t.id : null);
                  }}
                  style={{
                    width: "100%",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    padding: "7px 10px",
                    fontSize: 13,
                    outline: "none",
                    background: "#fafbff",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">
                    {templatesLoading
                      ? "Loading templates..."
                      : "-- Select a template --"}
                  </option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.template_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Template preview */}
              {templatePreview && (
                <div
                  style={{
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "12px 13px",
                    fontSize: 12,
                    color: "#374151",
                    flex: 1,
                    overflowY: "auto",
                    minHeight: 100,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#555",
                      marginBottom: 6,
                      fontSize: 12,
                    }}
                  >
                    Subject: {templatePreview.subject}
                  </div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(templatePreview.body),
                    }}
                    style={{ lineHeight: 1.6, pointerEvents: "none" }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Bottom actions */}
        <div
          style={{
            padding: "12px 16px 16px",
            borderTop: "1px solid #f0f0f0",
            flexShrink: 0,
          }}
        >
          {/* Attachment row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                cursor: "pointer",
                padding: "5px 11px",
                color: "#555",
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <PaperClipOutlined />
              {attachFiles.length > 0
                ? `${attachFiles.length} file${attachFiles.length > 1 ? "s" : ""} attached`
                : "Attach Files"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) =>
                setAttachFiles(Array.from(e.target.files || []))
              }
            />
            {attachFiles.length > 0 && (
              <button
                onClick={() => {
                  setAttachFiles([]);
                  if (fileInputRef.current)
                    fileInputRef.current.value = "";
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#ef4444",
                  fontSize: 11,
                  padding: 0,
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={!canSend}
            style={{
              width: "100%",
              background: canSend
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "#d1d5db",
              border: "none",
              borderRadius: 8,
              color: "#fff",
              cursor: canSend ? "pointer" : "not-allowed",
              padding: "10px 0",
              fontSize: 14,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              transition: "background 0.15s",
            }}
          >
            {sending ? (
              <Spin size="small" style={{ filter: "brightness(10)" }} />
            ) : (
              <SendOutlined />
            )}
            {sending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}
